/**
 * Form Validators Utility
 *
 * Pure validation functions that return { isValid: boolean, message?: string }.
 * Used by FormValidationController and reusable across the application.
 */

/**
 * Validate email address format
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateEmail(value) {
  return { isValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) }
}

/**
 * Validate phone number (international format, digits only, 10-16 digits)
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validatePhone(value) {
  return { isValid: /^[\+]?[1-9]\d{0,15}$/.test(value.replace(/\D/g, '')) }
}

/**
 * Validate Brazilian CEP (ZIP code): exactly 8 digits, not all zeros or repeating
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateZipCode(value) {
  if (!value) return { isValid: false }

  const clean = value.replace(/\D/g, '')
  if (clean.length !== 8) return { isValid: false }
  if (/^\d\1{7}$/.test(clean)) return { isValid: false }

  return { isValid: true }
}

/**
 * Validate Brazilian CNPJ format: XX.XXX.XXX/XXXX-XX
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateCnpj(value) {
  return { isValid: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value) }
}

/**
 * Validate Brazilian CPF format: XXX.XXX.XXX-XX
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateCpf(value) {
  return { isValid: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value) }
}

/**
 * Validate ISO 4217 currency code (exactly 3 uppercase letters)
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateCurrency(value) {
  return { isValid: /^[A-Z]{3}$/.test(value) }
}

/**
 * Validate latitude (-90 to 90)
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateLatitude(value) {
  const num = parseFloat(value)
  return { isValid: !isNaN(num) && num >= -90 && num <= 90 }
}

/**
 * Validate longitude (-180 to 180)
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateLongitude(value) {
  const num = parseFloat(value)
  return { isValid: !isNaN(num) && num >= -180 && num <= 180 }
}

/**
 * Validate date string is parseable
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateDate(value) {
  return { isValid: !isNaN(new Date(value).getTime()) }
}

/**
 * Validate date is today or in the future
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateDateFuture(value) {
  const selected = new Date(value)
  if (isNaN(selected.getTime())) return { isValid: false }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return { isValid: selected >= today }
}

/**
 * Validate value is a finite number
 * @param {string} value
 * @returns {{ isValid: boolean }}
 */
export function validateNumber(value) {
  return { isValid: !isNaN(parseFloat(value)) }
}

/**
 * Validate file type against allowed extensions
 * @param {File} file
 * @param {string[]} [allowedTypes] - e.g., ['.jpg', '.png']
 * @returns {{ isValid: boolean }}
 */
export function validateFileType(file, allowedTypes) {
  if (!allowedTypes || !allowedTypes.length) return { isValid: true }

  const ext = '.' + file.name.split('.').pop().toLowerCase()
  return { isValid: allowedTypes.includes(ext) }
}

/**
 * Validate file size against maximum bytes
 * @param {File} file
 * @param {number} [maxBytes] - max size in bytes
 * @returns {{ isValid: boolean }}
 */
export function validateFileSize(file, maxBytes) {
  if (!maxBytes) return { isValid: true }
  return { isValid: file.size <= maxBytes }
}

/**
 * Registry of named validators used by FormValidationController's runValidator
 */
export const Validators = {
  email: validateEmail,
  phone: validatePhone,
  zipCode: validateZipCode,
  cep: validateZipCode,
  cnpj: validateCnpj,
  cpf: validateCpf,
  currency: validateCurrency,
  latitude: validateLatitude,
  longitude: validateLongitude,
  date: validateDate,
  dateFuture: validateDateFuture,
  number: validateNumber
}
